/**
 * HelpPanel — Reusable game help/rules modal
 *
 * Displays game rules, controls, and tips in a slide-out panel.
 * Each game provides its own content via props.
 */

import React, { useState, useEffect } from 'react';
import './HelpPanel.css';

export interface HelpSection {
  title: string;
  content: string | string[];
  icon?: string;
}

export interface HelpPanelProps {
  gameName: string;
  sections: HelpSection[];
  isOpen: boolean;
  onClose: () => void;
  accentColor?: string;
}

export const HelpPanel: React.FC<HelpPanelProps> = ({
  gameName,
  sections,
  isOpen,
  onClose,
  accentColor = '#6366f1',
}) => {
  const [activeSection, setActiveSection] = useState(0);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="help-panel-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label={`${gameName} Help`}>
      <div className="help-panel" onClick={(e) => e.stopPropagation()}>
        <div className="help-panel__header" style={{ borderBottomColor: accentColor }}>
          <h2 className="help-panel__title">📖 {gameName} — How to Play</h2>
          <button className="help-panel__close" onClick={onClose} aria-label="Close help">✕</button>
        </div>

        <div className="help-panel__nav">
          {sections.map((section, i) => (
            <button
              key={i}
              className={`help-panel__tab ${activeSection === i ? 'active' : ''}`}
              onClick={() => setActiveSection(i)}
              style={activeSection === i ? { borderBottomColor: accentColor, color: accentColor } : {}}
            >
              {section.icon && <span className="help-panel__tab-icon">{section.icon}</span>}
              {section.title}
            </button>
          ))}
        </div>

        <div className="help-panel__content">
          {sections[activeSection] && (
            <div className="help-panel__section">
              <h3>{sections[activeSection].icon} {sections[activeSection].title}</h3>
              {Array.isArray(sections[activeSection].content) ? (
                <ul className="help-panel__list">
                  {(sections[activeSection].content as string[]).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className="help-panel__text">{sections[activeSection].content as string}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpPanel;
